import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { fetchComments } from '../services/commentService';
import { COMMENTS_REFETCH_INTERVAL_MS, commentsQueryKey } from '../config';
import type { Comment } from '../types';

interface UseNoteCommentsArgs {
  noteId?: string;
  shareToken?: string | null;
  enabled?: boolean;
}

/**
 * Fetch and cache a note's comment tree. Polls on an interval and refetches on
 * window focus so collaborators' comments appear without a manual refresh.
 *
 * The cache key is scoped by viewer identity (authenticated user id, else the
 * share token) so the singleton React Query cache can't serve one viewer's
 * comments to another on the same tab — e.g. after sign-out/sign-in, or a user
 * without access landing on the same noteId (whose 403 refetch would otherwise
 * keep showing the prior viewer's cached data). Mirrors the user-scoped key
 * pattern in useOrganizations. `enabled` waits until the identity resolves so we
 * never write comments into an `undefined`-keyed slot. The panel and timeline
 * markers compute the same viewer key, so they still share one fetch.
 */
export const useNoteComments = ({ noteId, shareToken, enabled = true }: UseNoteCommentsArgs) => {
  const userId = useAuthStore((s) => s.userData?.id);
  const userToken = useAuthStore((s) => s.userToken);

  const isAuthenticated = Boolean(userToken && userId);
  const viewerKey = isAuthenticated ? userId : (shareToken ?? null);
  const canFetch = enabled && Boolean(noteId) && Boolean(viewerKey);

  return useQuery<Comment[]>({
    queryKey: [...commentsQueryKey(noteId), viewerKey],
    queryFn: () => fetchComments({ noteId: noteId as string, shareToken, isAuthenticated }),
    enabled: canFetch,
    refetchInterval: COMMENTS_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
};
